import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { directAuth } from '../../services/directAuth';
import GlobalNavBar from '../layout/GlobalNavBar';

interface ForgotPasswordFormData {
  email: string;
}

export const ForgotPasswordForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setApiError(null);
    try {
      const response = await directAuth.forgotPassword(data.email);

      if (response.success) {
        setSubmittedEmail(data.email);
        setIsSubmitted(true);
        // Render the success toast via i18n so it honors the active locale.
        toast.success(t('auth:resetCodeSentDesc'));
      } else {
        const key = response.errorCode
          ? `auth:resetErrors.${response.errorCode}`
          : 'auth:errors.forgotPasswordFailed';
        // `t()` falls back to the default namespace key if the specific
        // errorCode entry is missing, keeping this resilient to new Cognito
        // error codes we haven't mapped yet.
        const message = t(key, {
          defaultValue: t('auth:errors.forgotPasswordFailed'),
        });
        setApiError(message);
        toast.error(message);
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      const message = t('auth:errors.forgotPasswordFailed');
      setApiError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueToReset = () => {
    navigate('/reset-password', { state: { email: submittedEmail } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" maxWidth="max-w-7xl" />

      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {isSubmitted
                ? t('auth:resetCodeSentTitle')
                : t('auth:forgotPasswordTitle')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {isSubmitted
                ? t('auth:resetCodeSentDesc')
                : t('auth:forgotPasswordDesc')}
            </p>
          </div>

          {!isSubmitted ? (
            <form
              className="mt-8 space-y-6"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth:emailAddress')}
                </label>
                <input
                  id="email"
                  {...register('email', {
                    required: t('auth:errors.emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('auth:errors.invalidEmail'),
                    },
                  })}
                  type="email"
                  autoComplete="email"
                  disabled={isLoading}
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  className="mt-1 input"
                  placeholder={t('auth:enterEmail')}
                />
                {errors.email && (
                  <p
                    id="email-error"
                    className="mt-1 text-sm text-red-600"
                    role="alert"
                  >
                    {errors.email.message}
                  </p>
                )}
              </div>

              {apiError && (
                <div
                  className="p-3 bg-red-50 border border-red-200 rounded-md"
                  role="alert"
                  aria-live="assertive"
                >
                  <p className="text-sm text-red-600">{apiError}</p>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  aria-busy={isLoading}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading
                    ? t('auth:sendingResetCode')
                    : t('auth:sendResetCode')}
                </button>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  {t('auth:backToLogin')}
                </Link>
              </div>
            </form>
          ) : (
            <div className="mt-8 space-y-6">
              <div
                className="p-4 bg-green-50 border border-green-200 rounded-md"
                role="status"
                aria-live="polite"
              >
                <p className="text-sm text-green-700">
                  {t('auth:resetCodeSentDesc')}
                </p>
              </div>

              <button
                type="button"
                onClick={handleContinueToReset}
                className="w-full btn-primary"
              >
                {t('auth:continueToReset')}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  {t('auth:backToLogin')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
